import { memo } from 'react';
import type { ExpenseDTO, ExpenseView } from '@six-pockets/shared';
import { PencilIcon, TrashIcon } from '@/components/icons';
import { Button } from '@/components/ui';
import { cx } from '@/utils/cx';
import { formatDateTime, formatINR } from '@/utils/format';
import { SOURCE_LABEL, WITHDRAWAL_LABEL } from '../sources';
import styles from '@/styles/components/LedgerRow/style.scss';

interface ExpenseRowProps {
  expense: ExpenseDTO;
  /** Expenses: when spent, with Withdraw / edit / delete. Withdrawals: when spent and when withdrawn, no actions. */
  view: ExpenseView;
  onEdit: (e: ExpenseDTO) => void;
  onDelete: (e: ExpenseDTO) => void;
  onWithdraw: (e: ExpenseDTO) => void;
}

/** One expense: reason, who paid and when; amount; then Withdraw (paid by someone), edit, delete, or "Withdrawn". */
export const ExpenseRow = memo(function ExpenseRow({ expense: e, view, onEdit, onDelete, onWithdraw }: ExpenseRowProps) {
  const withdrawn = e.withdrawnAt !== null;
  const waiver = e.source === 'waiver';
  const personal = e.source !== 'counter' && !waiver;

  return (
    <li className={cx(styles.row, withdrawn && view === 'expenses' && styles.settled)}>
      <div className={styles.main}>
        <span className={styles.title} title={e.reason}>
          {e.reason}
        </span>
        <span className={styles.meta}>
          <span className={cx(styles.source, personal && styles.person, waiver && styles.waiver)}>{SOURCE_LABEL[e.source]}</span>
          {view === 'withdrawals' ? `spent ${formatDateTime(e.createdAt)}` : formatDateTime(e.createdAt)}
        </span>
      </div>

      <span className={styles.amount}>{formatINR(e.amount)}</span>

      <div className={styles.actions}>
        {waiver ? (
          // Made by Pay now: it stands for what was let off on these bills, so it isn't edited, deleted or withdrawn.
          <span className={styles.withdrawn}>
            {e.waiver?.customerName}
            {e.waiver?.billNumbers.length ? ` · ${e.waiver.billNumbers.map((n) => `#${n}`).join(', ')}` : ''}
          </span>
        ) : withdrawn ? (
          <span className={styles.withdrawn}>
            Withdrawn {formatDateTime(e.withdrawnAt!)}
            {e.withdrawnFrom && ` · from ${WITHDRAWAL_LABEL[e.withdrawnFrom].toLowerCase()}`}
          </span>
        ) : (
          <>
            <Button size="sm" variant="ghost" iconOnly icon={<PencilIcon size={15} />} aria-label={`Edit ${e.reason}`} title="Edit" onClick={() => onEdit(e)} />
            <Button size="sm" variant="ghost" iconOnly icon={<TrashIcon size={15} />} aria-label={`Delete ${e.reason}`} title="Delete" onClick={() => onDelete(e)} />
            {personal && (
              <Button size="sm" variant="primary" onClick={() => onWithdraw(e)}>
                Withdraw
              </Button>
            )}
          </>
        )}
      </div>
    </li>
  );
});
